import styled from '@emotion/styled';
import {
  GameTypeEnum,
  MeWithTutorialQuery_me_company_gamesConfig,
} from 'graphql/types';
import colors from 'lib/colors';
import { ScenesProgressContext } from 'pages/Lobby/ScenesProgressProvider';
import { useContext, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';

interface Props {
  skipSimpleHeading?: boolean;
  gameType: GameTypeEnum;
  showRequirementsOnly?: boolean;
  colorVariant?: 'default' | 'black';
  gamesConfig?: MeWithTutorialQuery_me_company_gamesConfig[];
}

const SIMPLE_HEADING_TYPES = [GameTypeEnum.FREE_GAME];

const ScenesProgressHeading = ({
  skipSimpleHeading,
  gameType,
  showRequirementsOnly = false,
  colorVariant,
  gamesConfig,
}: Props) => {
  const { t } = useTranslation();
  const { allScenesCompleted, trainingRequirements } = useContext(
    ScenesProgressContext,
  );

  const mediumGameActive = useMemo(() => {
    return (
      gamesConfig?.find((config) => config.code === 'MEDIUM_GAME')?.active ??
      true
    );
  }, [gamesConfig]);

  if (!skipSimpleHeading && SIMPLE_HEADING_TYPES.includes(gameType)) {
    return <Heading>{t('matchView.title', 'Dějiště k dispozici')}</Heading>;
  }

  const {
    finishedGamesInScene,
    finishAgainstPlayer,
    winAgainstCPU,
    percentageOfEliminatedAttacks,
  } = trainingRequirements || {};
  const prefix = 'matchView.trainingRequirements.title.';
  let easyGamePart;
  let mediumGamePart1;
  let mediumGamePart2;
  easyGamePart = prefix + 'easyGamePart';
  if ((finishAgainstPlayer || winAgainstCPU) && mediumGameActive) {
    mediumGamePart1 = prefix + 'part1.';
    mediumGamePart2 = prefix + 'part2.';

    mediumGamePart1 += 'onlyMedium';
    if (finishAgainstPlayer && winAgainstCPU) {
      mediumGamePart2 += 'finishAgainstPlayerOrWinAgainstCPU';
    } else if (finishAgainstPlayer) {
      mediumGamePart2 += 'finishAgainstPlayer';
    } else {
      mediumGamePart2 += 'winAgainstCPU';
    }
  }

  return (
    <Heading colorVariant={colorVariant}>
      {allScenesCompleted && !showRequirementsOnly ? (
        <Trans
          t={t}
          i18nKey="matchView.trainingCompleted"
          count={finishedGamesInScene}
          components={{ b: <b /> }}
        />
      ) : (
        <>
          <Trans
            t={t}
            i18nKey={prefix + 'commonPart'}
            defaults={'Ke splnění školení musíte pro každé dějiště'}
            components={{ b: <b /> }}
          />
          {easyGamePart && (
            <Trans
              t={t}
              i18nKey={easyGamePart}
              defaults={
                '<b>{{finishedGamesInScene}}x v základní hře odvrátit {{percentageOfEliminatedAttacks}}% útoků</b>'
              }
              values={{ finishedGamesInScene, percentageOfEliminatedAttacks }}
              components={{ b: <b /> }}
            />
          )}
          {mediumGamePart1 && (
            <Trans
              t={t}
              i18nKey={mediumGamePart1}
              defaults={', nebo v pokročilé hře'}
              components={{ b: <b /> }}
            />
          )}
          {mediumGamePart2 && (
            <Trans
              t={t}
              i18nKey={mediumGamePart2}
              defaults={'<b>porazit počítač nebo dohrát hru s kolegou</b>'}
              components={{ b: <b /> }}
            />
          )}
        </>
      )}
    </Heading>
  );
};

export default ScenesProgressHeading;

const Heading = styled('div')<{ colorVariant?: 'default' | 'black' }>(
  ({ colorVariant }) => ({
    width: '100%',
    textAlign: 'center',
    color: colorVariant === 'black' ? colors.black : colors.lobbyText,
    '& b': {
      color: colorVariant === 'black' ? colors.black : colors.white,
    },
  }),
);
